import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { CHARTER_PAGE_SLUG } from 'constants/charterFlight'
import { LOYALTY_PURCHASE_POINTS_PAGE } from 'loyalty/constants/loyaltyRoutes'
import LoyaltyRewardsIcon from 'LuxKit/Icons/loyalty/LoyaltyRewardsIcon'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineIslandIcon from 'LuxKit/Icons/line/LineIslandIcon'
import LinePlaneFlyIcon from 'LuxKit/Icons/line/LinePlaneFlyIcon'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useMemo, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

const StepsGrid = styled.div`
  display: grid;
  gap: ${rem(40)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
`

const StepIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${rem(6)};
  align-self: flex-start;
  padding: ${rem(20)};
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

interface HowToFindStep {
  icon: ReactNode
  title: string
  description: ReactNode
}

function LoyaltyRewardsHowToFindSection() {
  const intl = useIntl()

  const heading = intl.formatMessage({
    id: '3W1uWM5uJEscXjJny-O0',
    defaultMessage: 'How to book Société Rewards',
    description: 'Section heading explaining how to book Société Rewards on the rewards landing page',
  })

  const browseBundlesLabel = intl.formatMessage({
    id: 'r7KgP92evb8F8DTe3zSF',
    defaultMessage: 'Browse Maldives Bundles',
    description: 'Primary call-to-action button in the Société rewards page hero',
  })

  const pointsLabel = intl.formatMessage({
    id: 'vsQfqFkzuNex-CDHSHpv',
    defaultMessage: 'Société Points',
    description: 'Société Points label in the points plus cash icon pill on the Société rewards page',
  })

  const cashLabel = intl.formatMessage({
    id: 'B5-kzaPLSgnu-FGEwT0i',
    defaultMessage: '+ cash',
    description: 'Cash label in the points plus cash icon pill on the Société rewards page',
  })

  const plusLabel = intl.formatMessage({
    id: 'ujnJnDPK1jBB9Jv1b14F',
    defaultMessage: '+',
    description: 'Plus sign separator between travel product icons on the Société rewards page',
  })

  const steps = useMemo<Array<HowToFindStep>>(() => [
    {
      icon: <Group direction="horizontal" gap={8} verticalAlign="center">
        <LinePlaneFlyIcon size="L" />
        <BodyText variant="medium">{plusLabel}</BodyText>
        <LineBedIcon size="L" />
        <BodyText variant="medium">{plusLabel}</BodyText>
        <LineIslandIcon size="L" />
      </Group>,
      title: intl.formatMessage({
        id: 'Zp3F0FLZ0k63Men_RYjM',
        defaultMessage: 'Browse Maldives bundle offers',
        description: 'Step title for browsing Maldives bundle offers on the Société rewards page',
      }),
      description: intl.formatMessage({
        id: 'FDj3zei7geE9mAJDE0XH',
        defaultMessage: 'The only direct flights from Australia to Malé, paired with exclusive Maldives hotel stays.',
        description: 'Step description for browsing Maldives bundle offers on the Société rewards page',
      }),
    },
    {
      icon: <LoyaltyRewardsIcon size={32} colour="highlight-primary-dark" />,
      title: intl.formatMessage({
        id: '7bS8m0aBK7-ootWG6f64',
        defaultMessage: 'Look for the Rewards badge',
        description: 'Step title for finding the rewards badge on the Société rewards page',
      }),
      description: intl.formatMessage({
        id: 'tKBswORBC-OvocXCOXF4',
        defaultMessage: 'In the calendar, look for dates marked with the Société Rewards badge. These sell out fast, so check availability early.',
        description: 'Step description explaining the Société Rewards badge in the calendar',
      }),
    },
    {
      icon: <Group direction="horizontal" gap={8} verticalAlign="center">
        <LoyaltyTierIcon tier="bronze" size={32} />
        <Group direction="horizontal" gap={4} verticalAlign="center">
          <BodyText as="span" variant="medium" weight="bold" colour="tier-bronze-dark">{pointsLabel}</BodyText>
          <BodyText as="span" variant="medium">{cashLabel}</BodyText>
        </Group>
      </Group>,
      title: intl.formatMessage({
        id: 'U22P6-GpSBqJROX5pwle',
        defaultMessage: 'Pay with Société Points',
        description: 'Step title for paying with Société Points on the rewards landing page',
      }),
      description: intl.formatMessage({
        id: '0lMOkEjrlcGehjDvlzbM',
        defaultMessage: 'Redeem your Société Points for the flight, then pay for your hotel package and taxes & fees. Need more points? <link>Top-up points</link>',
        description: 'Step description for paying with Société Points, including a link to buy more points',
      }, {
        link: (chunks: ReactNode) => <TextLink key="buy-now" to={`/${LOYALTY_PURCHASE_POINTS_PAGE}`} target="_blank" weight="regular">{chunks}</TextLink>,
      }),
    },
  ], [cashLabel, intl, plusLabel, pointsLabel])

  return <Pane background="neutral-seven" padding="48 0">
    <LayoutContainer variant="medium">
      <Group direction="vertical" gap={48} horizontalAlign="center">
        <Heading variant="heading2" as="h2">{heading}</Heading>
        <StepsGrid>
          {steps.map(step => (
            <VerticalSpacer key={step.title} gap={16}>
              <StepIconWrapper>{step.icon}</StepIconWrapper>
              <VerticalSpacer gap={4}>
                <Heading variant="heading5">{step.title}</Heading>
                <BodyText variant="large">{step.description}</BodyText>
              </VerticalSpacer>
            </VerticalSpacer>
          ))}
        </StepsGrid>
        <TextButton kind="primary" size="large" to={`/campaign/${CHARTER_PAGE_SLUG}`}>
          {browseBundlesLabel}
        </TextButton>
      </Group>
    </LayoutContainer>
  </Pane>
}

export default LoyaltyRewardsHowToFindSection
